import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import type { UserRole } from '@/types/route'
import { resolveMediaUrl } from '@/lib/media'

const ACCESS_TOKEN_KEY = 'token'
const REFRESH_TOKEN_KEY = 'refreshToken'

export interface User {
  id: string
  username: string
  qqNumber: string
  avatar?: string
  role: UserRole
  allowResumeTrust?: boolean
  allowWebLoginTrust?: boolean
  maxGameAccounts?: number
  maxSkins?: number
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  refreshToken: string | null
  isHydrated: boolean
  setAuth: (user: User, token: string, refreshToken?: string | null) => void
  updateTokens: (token: string, refreshToken?: string | null) => void
  clearAuth: () => void
  updateUser: (user: Partial<User>) => void
  setHydrated: (hydrated: boolean) => void
  isAdmin: () => boolean
}

function normalizeUser(user: User | Record<string, unknown>): User {
  const source = user as Partial<User> & Record<string, unknown>

  return {
    id: String(source.id ?? ''),
    username: String(source.username ?? ''),
    qqNumber: String(source.qqNumber ?? source['qq_number'] ?? ''),
    avatar: resolveMediaUrl(String(source.avatar || '')),
    role: (source.role as UserRole) || 'user',
    allowResumeTrust:
      Boolean(source.allowResumeTrust ?? source['allow_resume_trust'] ?? true),
    allowWebLoginTrust:
      Boolean(source.allowWebLoginTrust ?? source['allow_web_login_trust'] ?? true),
    maxGameAccounts: Number(
      source.maxGameAccounts ?? source['max_game_accounts'] ?? 1
    ),
    maxSkins: Number(source.maxSkins ?? source['max_skins'] ?? 30),
  }
}

function persistTokens(token: string | null, refreshToken: string | null) {
  if (typeof window === 'undefined') {
    return
  }

  if (token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token)
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  }
}

export function getStoredAccessToken() {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  } catch {
    return null
  }
}

export function getStoredRefreshToken() {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  } catch {
    return null
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
      isHydrated: false,

      isAdmin: () => get().user?.role === 'admin',

      setAuth: (user, token, refreshToken = null) => {
        persistTokens(token, refreshToken)
        set({
          isAuthenticated: true,
          user: normalizeUser(user),
          token,
          refreshToken,
        })
      },

      updateTokens: (token, refreshToken) => {
        const nextRefreshToken = refreshToken ?? get().refreshToken ?? null
        persistTokens(token, nextRefreshToken)
        set({
          token,
          refreshToken: nextRefreshToken,
          isAuthenticated: true,
        })
      },

      clearAuth: () => {
        persistTokens(null, null)
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          refreshToken: null,
        })
      },

      updateUser: userData => {
        set(state => ({
          user: state.user ? normalizeUser({ ...state.user, ...userData }) : null,
        }))
      },

      setHydrated: hydrated => {
        set({ isHydrated: hydrated })
      },
    }),
    {
      name: 'dreamrealms-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isHydrated: state.isHydrated,
      }),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('Auth store rehydration failed:', error)
          }

          if (state) {
            setTimeout(() => {
              persistTokens(state.token, state.refreshToken)
              if (state.user) {
                state.updateUser(state.user)
              }
              state.setHydrated(true)
            }, 0)
          }
        }
      },
    }
  )
)
